// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2026 Ben Richardson <hi@ben.gy>
// Additional terms apply under AGPL-3.0 section 7(b) — see ADDITIONAL-TERMS.md

/*
 * Pulling the quotations out of the draft.
 *
 * This is where the error budget actually goes. Matching is arithmetic and can
 * be made correct; deciding what counts as a quotation is a judgement call made
 * thousands of times per document, and every wrong call either invents a
 * quotation that was never claimed or misses one that was.
 *
 * THE APOSTROPHE RULE IS THE IMPORTANT ONE. A straight ' is NEVER a delimiter,
 * and a curly U+2019 is only ever a closer for an actual U+2018 opener. Counted
 * on the real text of Darwin's Origin: 147 instances of U+2019, of which 128 are
 * word-internal apostrophes and 12 are possessives or ambiguous closers, against
 * 9 genuine U+2018 openers. Treating U+2019 as an opening delimiter would
 * manufacture roughly fifteen quotations per chapter out of ordinary
 * possessives.
 *
 * Everything here is delimiter-anchored or explicitly marked. Nothing is
 * inferred from "this sentence looks quoted".
 */

import type { DraftQuote, Pinpoint } from '../types.ts';

interface Pair {
  open: string;
  close: string[];
  via: DraftQuote['via'];
}

const PAIRS: Pair[] = [
  { open: '“', close: ['”', '“'], via: 'double' }, // “ ”
  { open: '„', close: ['“', '”'], via: 'double' }, // „ “  (German)
  { open: '«', close: ['»'], via: 'guillemet' }, // « »
  { open: '「', close: ['」'], via: 'cjk' }, // 「 」
  { open: '『', close: ['』'], via: 'cjk' }, // 『 』
  { open: '‘', close: ['’'], via: 'single' }, // ‘ ’ — opener must be real
];

/** The longest run of characters we will accept as one quotation. */
const MAX_QUOTE_CHARS = 3000;

const WORD_CHAR = /[\p{L}\p{N}]/u;

/**
 * Can this occurrence of a closing delimiter really be closing a quotation?
 *
 * The header above counts 128 of Darwin's 147 U+2019s as word-internal, and
 * that is precisely the population that used to break this: the closer search
 * took the NEAREST following ’, so in UK/Commonwealth single-quote house style
 * a quotation was truncated at its first possessive or contraction. Two
 * measured outcomes, both against sources containing the passage verbatim:
 * ‘Natural selection … superior to man’s feeble efforts …’ was cut at "man"
 * and reported as a NEAR MATCH with "man" rendered as an inserted word — the
 * report accusing the author of a misquotation the extractor had committed —
 * and ‘we don’t know whether …’ was cut to "we don" and dropped below the
 * eight-word floor, so a real sixteen-word quotation was never audited and
 * nothing said so.
 *
 * A U+2019 flanked by word characters on both sides is an apostrophe. Nothing
 * else in PAIRS doubles as a letter, so only U+2019 is tested; if every
 * candidate is word-internal the quotation is treated as unterminated, which is
 * what an unpaired opener already did.
 */
function closesAQuotation(raw: string, index: number, ch: string): boolean {
  if (ch !== '’') return true;
  const before = index > 0 ? raw[index - 1] : '';
  const after = index + 1 < raw.length ? raw[index + 1] : '';
  return !(WORD_CHAR.test(before) && WORD_CHAR.test(after));
}

/** How far past a quotation we look for a pinpoint locator. */
const PINPOINT_WINDOW = 160;

const ROMAN_RE = /^(m{0,3})(cm|cd|d?c{0,3})(xc|xl|l?x{0,3})(ix|iv|v?i{0,3})$/i;

export function romanToInt(s: string): number | null {
  const t = s.toLowerCase();
  if (!t.length || !ROMAN_RE.test(t)) return null;
  const map: Record<string, number> = { i: 1, v: 5, x: 10, l: 50, c: 100, d: 500, m: 1000 };
  let total = 0;
  for (let i = 0; i < t.length; i++) {
    const v = map[t[i]];
    const next = map[t[i + 1]] ?? 0;
    total += v < next ? -v : v;
  }
  return total;
}

/**
 * Split a quotation at its ellipses.
 *
 * An ellipsis means the author cut something out, so the two sides are not
 * contiguous in the source and must never be aligned as though they were —
 * running an edit distance across the gap charges the omitted words as errors
 * and turns a scrupulously marked elision into a misquotation.
 */
export function splitOnEllipsis(text: string): string[] {
  const parts = text
    .split(/\s*(?:\[\s*(?:\.{2,}|…)\s*\]|\.\s*\.\s*\.+|\.{2,}|…)\s*/g)
    .map((s) => s.trim())
    .filter((s) => s.length > 0);
  return parts.length > 0 ? parts : [text.trim()];
}

/**
 * Remove short editorial insertions.
 *
 * "[sic]", "[emphasis added]" and "[his]" are the author speaking, not the
 * source, so they must not be charged as inserted words. Anything longer than
 * four words is left alone — at that length it is a substantive interpolation
 * and the reader should see it charged.
 */
export function stripEditorial(text: string): string {
  return text
    .replace(/\[([^\][]{1,60})\]/g, (whole, inner: string) => {
      const words = inner.trim().split(/\s+/).filter(Boolean);
      return words.length <= 4 ? ' ' : whole;
    })
    .replace(/\s{2,}/g, ' ')
    .trim();
}

/** A locator immediately followed by "-412", i.e. the first page of a range. */
const RANGE_TAIL = /^\s*[-–—]\s*([ivxlcdm]+|\d{1,5})\b/i;

/**
 * The closing locator of `pp. 411-412`, as a number.
 *
 * Returns null when there is no range, and also when the range is written in a
 * different notation from its opening locator (`pp. xi-14`), which is more
 * likely a typo or a hyphenated compound than a real span — and a wrong `end`
 * would widen the accepted window and hide a genuinely wrong citation.
 * `elided` handles the common `pp. 411-12` shorthand, where the closing page
 * repeats the opening page's leading digits.
 */
function rangeEnd(tail: string, from: number, roman: boolean): number | null {
  const m = RANGE_TAIL.exec(tail);
  if (!m) return null;
  const token = m[1];
  const isDigits = /^\d+$/.test(token);
  if (isDigits === roman) return null;
  if (roman) {
    const value = romanToInt(token);
    return value !== null && value > from ? value : null;
  }
  const literal = parseInt(token, 10);
  if (literal > from) return literal;
  // Elision ("411-12" for 411-412) is only a style rule from 100 upwards —
  // below that every style writes both pages in full. So a two-digit opening
  // page with a smaller closing number is not an elided range: "pp. 14-9" is a
  // typo or a hyphenated construct, and reading it as 14-19 would widen the
  // accepted window on a guess.
  const head = String(from);
  if (head.length < 3 || token.length >= head.length) return null;
  const elided = parseInt(head.slice(0, head.length - token.length) + token, 10);
  return elided > from ? elided : null;
}

/**
 * Find "p. 14", "at 1207", "¶ 32", "§ 3" in the citation next to a quotation.
 *
 * `inCitation` says the text really is a citation — a bracketed group, or a
 * locator sitting immediately after the closing delimiter — rather than the
 * prose that happens to follow. Only there is the bare "at N" form read.
 */
export function findPinpoint(text: string, inCitation = false): Pinpoint | null {
  const patterns: { re: RegExp; kind: Pinpoint['kind'] }[] = [
    // The separator after "p"/"pp" is REQUIRED. It used to be optional on both
    // the full stop and the space, which made ordinary prose — "the p53
    // pathway" — parse as page 53 and hang a "pinpoint differs" accusation on a
    // verbatim-correct quotation that carried no page citation at all.
    { re: /\bpp?(?:\.\s*|\s+)([ivxlcdm]+|\d{1,5})\b/i, kind: 'page' },
    { re: /\bpages?\s+([ivxlcdm]+|\d{1,5})\b/i, kind: 'page' },
  ];
  // "at 1207" is the law-report pinpoint style, but as a bare pattern it also
  // reads "developed at 3 later stages" and "he was writing at 50" as page
  // numbers, so it is confined to an actual citation.
  if (inCitation) patterns.push({ re: /\bat\s+(\d{1,5})\b/i, kind: 'page' });
  patterns.push(
    { re: /[¶]\s*(\d{1,5})\b/, kind: 'paragraph' },
    { re: /\bparas?\.?\s*(\d{1,5})\b/i, kind: 'paragraph' },
    { re: /[§]\s*(\d{1,5})\b/, kind: 'section' },
  );
  for (const { re, kind } of patterns) {
    const m = re.exec(text);
    if (!m) continue;
    // A range names several pages, and reading "pp. 411-412" as page 411 alone
    // called a correctly cited quotation wrong whenever the match landed on any
    // page of the range but the first — which is exactly what happens to the
    // quotation that spans a page break, the reason the range was cited. So the
    // closing page is carried too, and match.ts accepts any page in the span.
    // Where the closing page cannot be read confidently, `end` stays undefined
    // and the pinpoint is judged on its first page, as before.
    const tail = text.slice(m.index + m[0].length);
    const token = m[1];
    if (/^\d+$/.test(token)) {
      const value = parseInt(token, 10);
      const end = rangeEnd(tail, value, false);
      const raw = (end === null ? m[0] : text.slice(m.index, m.index + m[0].length + RANGE_TAIL.exec(tail)![0].length)).trim();
      return { raw, kind, value, roman: false, ...(end === null ? {} : { end }) };
    }
    const roman = romanToInt(token);
    // A bare "p. i" is far more often a stray letter than a pinpoint to the
    // first roman page, so require at least two numerals.
    if (roman !== null && roman >= 1 && roman <= 200 && token.length >= 2) {
      const end = rangeEnd(tail, roman, true);
      const raw = (end === null ? m[0] : text.slice(m.index, m.index + m[0].length + RANGE_TAIL.exec(tail)![0].length)).trim();
      return { raw, kind, value: roman, roman: true, ...(end === null ? {} : { end }) };
    }
  }
  return null;
}

/** The inside of the parenthetical or bracketed citation immediately after a quotation. */
function citationGroup(tail: string): string | null {
  const m = /^[\s.,;:]*[([]([^)\]]{2,120})[)\]]/.exec(tail);
  if (!m) return null;
  return m[1];
}

/**
 * The run of draft text that belongs to the quotation's own citation.
 *
 * The pinpoint used to be scraped from a flat 160-character slice of whatever
 * followed the closing delimiter, with no requirement that it be adjacent —
 * so `“…”. Malthus said much the same thing (Malthus 1798, p. 20).` put "Cited
 * p. 20" on a quotation that carried no page reference, and the next
 * sentence's page number became an accusation of miscitation written into
 * quote-audit.csv. Attribution was always anchored this way; the pinpoint was
 * not.
 *
 * The cut is the first sentence end outside brackets. A full stop that is
 * followed by whitespace and a digit is an abbreviation, not a sentence end —
 * that is exactly the "p. 14" and "¶ 32." this has to keep reading.
 *
 * A paragraph break cuts too, with one exception: a block quotation is cited on
 * the line BELOW it, as "— Malthus 1798, p. 11." or "(Malthus 1798, p. 11)".
 * That line is the quotation's own citation and has to be read, so a break is
 * crossed when what follows opens with an attribution dash or a bracket. A
 * plain hyphen is deliberately not on that list, because it opens a Markdown
 * list item rather than an attribution.
 */
function adjacentRun(tail: string): string {
  let depth = 0;
  for (let i = 0; i < tail.length; i++) {
    const ch = tail[i];
    if (ch === '(' || ch === '[') depth++;
    else if (ch === ')' || ch === ']') depth = Math.max(0, depth - 1);
    else if (ch === '\n' && tail[i + 1] === '\n') {
      if (!/^\s*[—–([]/.test(tail.slice(i + 1))) return tail.slice(0, i);
    } else if (depth === 0 && (ch === '.' || ch === '!' || ch === '?' || ch === ';')) {
      const rest = tail.slice(i + 1);
      if (rest.length === 0) return tail.slice(0, i);
      if (/^\s/.test(rest) && !/^\s+\d/.test(rest)) return tail.slice(0, i);
    }
  }
  return tail;
}

function countWords(text: string): number {
  const m = text.match(/[\p{L}\p{N}]+(?:[-'’][\p{L}\p{N}]+)*/gu);
  return m ? m.length : 0;
}

function isCjkHeavy(text: string): boolean {
  let cjk = 0;
  let letters = 0;
  for (const ch of text) {
    const cp = ch.codePointAt(0) as number;
    if (
      (cp >= 0x3040 && cp <= 0x30ff) ||
      (cp >= 0x3400 && cp <= 0x4dbf) ||
      (cp >= 0x4e00 && cp <= 0x9fff) ||
      (cp >= 0xac00 && cp <= 0xd7af)
    ) {
      cjk++;
      letters++;
    } else if (/[\p{L}\p{N}]/u.test(ch)) {
      letters++;
    }
  }
  return letters > 0 && cjk / letters > 0.4;
}

/**
 * Is this quotation long enough to adjudicate?
 *
 * Eight words is the floor the published decontamination recipes use, and it is
 * also where short quoted fragments stop being quotations and start being
 * titles, terms of art and scare quotes — "the 'replication crisis'" is not a
 * claim about what a page says. CJK has no spaces, so it is measured in
 * characters instead.
 */
export function meetsFloor(text: string): boolean {
  if (isCjkHeavy(text)) return text.replace(/\s+/g, '').length >= 16;
  return countWords(text) >= 8 || text.replace(/\s+/g, '').length >= 40;
}

/**
 * Extract every quotation from a draft.
 *
 * Nested quotations report only the OUTERMOST span: a quotation that itself
 * contains a quotation is one claim about one source, and reporting the inner
 * one separately would charge the author twice for the same sentence.
 */
export function extractQuotes(raw: string): DraftQuote[] {
  const found: DraftQuote[] = [];
  const claimed: boolean[] = new Array(raw.length).fill(false);

  const claim = (start: number, end: number): boolean => {
    for (let i = start; i < end; i++) if (claimed[i]) return false;
    for (let i = start; i < end; i++) claimed[i] = true;
    return true;
  };

  const push = (start: number, end: number, inner: string, via: DraftQuote['via']) => {
    const cleaned = stripEditorial(inner);
    if (!cleaned) return;
    const tail = raw.slice(end, Math.min(raw.length, end + PINPOINT_WINDOW));
    const cite = citationGroup(tail);
    const near = adjacentRun(tail);
    found.push({
      id: 0,
      text: cleaned,
      start,
      end,
      via,
      segments: splitOnEllipsis(cleaned),
      pinpoint:
        (cite !== null ? findPinpoint(cite, true) : null) ??
        findPinpoint(near, /^[\s.,;:]*at\s+\d/i.test(near)),
      attributedTo: cite === null ? null : cite.trim(),
    });
  };

  // THE BLOCK PASSES RUN FIRST, and that ordering is load-bearing. `claim` is
  // all-or-nothing over a range, so whichever pass runs first wins the whole
  // span. When the delimiter passes ran first they claimed any quoted phrase
  // sitting INSIDE a block quotation — `either "physical or moral"` — and the
  // enclosing block then failed to claim and was dropped in silence, leaving
  // only the three-word fragment, which is under the floor and so reported as
  // "not checked". A block quotation is the longest and most consequential
  // verbatim claim in a draft and an inner quoted phrase inside one is
  // entirely ordinary, so a fabricated block was passing the audit unremarked.
  // Running the blocks first is also what the outermost-span contract above
  // actually says.

  // ── Markdown blockquotes ──────────────────────────────────────────────────
  {
    const lineStarts: number[] = [0];
    for (let i = 0; i < raw.length; i++) if (raw[i] === '\n') lineStarts.push(i + 1);
    let i = 0;
    while (i < lineStarts.length) {
      const start = lineStarts[i];
      const lineEnd = i + 1 < lineStarts.length ? lineStarts[i + 1] - 1 : raw.length;
      if (!/^\s{0,3}>/.test(raw.slice(start, lineEnd))) {
        i++;
        continue;
      }
      let j = i;
      let end = lineEnd;
      const parts: string[] = [];
      while (j < lineStarts.length) {
        const s = lineStarts[j];
        const e = j + 1 < lineStarts.length ? lineStarts[j + 1] - 1 : raw.length;
        const line = raw.slice(s, e);
        if (!/^\s{0,3}>/.test(line)) break;
        parts.push(line.replace(/^\s{0,3}>\s?/, ''));
        end = e;
        j++;
      }
      const inner = parts.join(' ').replace(/\s+/g, ' ').trim();
      if (inner && claim(start, end)) push(start, end, inner, 'blockquote');
      i = j;
    }
  }

  // ── Indented block quotations ─────────────────────────────────────────────
  // A paragraph indented four spaces or a tab, of at least twelve words, with
  // no backtick anywhere in it. In an academic draft that is a block quotation;
  // the backtick test is what keeps it from eating a fenced code sample.
  {
    const paragraphs = raw.split(/\n\s*\n/);
    let cursor = 0;
    for (const para of paragraphs) {
      const start = raw.indexOf(para, cursor);
      cursor = start + para.length;
      if (start < 0) continue;
      if (!/^(?:\t| {4,})\S/.test(para)) continue;
      if (para.includes('`')) continue;
      const inner = para.replace(/\s+/g, ' ').trim();
      if (countWords(inner) < 12) continue;
      if (claim(start, start + para.length)) push(start, start + para.length, inner, 'marked');
    }
  }

  // ── Paired delimiters ──────────────────────────────────────────────────────
  for (const pair of PAIRS) {
    let at = 0;
    while (at < raw.length) {
      const open = raw.indexOf(pair.open, at);
      if (open === -1) break;
      let close = -1;
      for (const c of pair.close) {
        let cand = raw.indexOf(c, open + 1);
        while (cand !== -1 && !closesAQuotation(raw, cand, c)) cand = raw.indexOf(c, cand + 1);
        if (cand !== -1 && (close === -1 || cand < close)) close = cand;
      }
      if (close === -1 || close - open > MAX_QUOTE_CHARS) {
        at = open + 1;
        continue;
      }
      if (claim(open, close + 1)) push(open, close + 1, raw.slice(open + 1, close), pair.via);
      at = close + 1;
    }
  }

  // ── Straight double quotes ────────────────────────────────────────────────
  // Handled after the curly pairs so a document that mixes both does not have
  // its typographic quotations re-detected as straight ones. Strictly paired:
  // an odd trailing quote is ignored rather than swallowing the rest of the
  // document.
  {
    const positions: number[] = [];
    for (let i = 0; i < raw.length; i++) if (raw[i] === '"' && !claimed[i]) positions.push(i);
    for (let p = 0; p + 1 < positions.length; p += 2) {
      const open = positions[p];
      const close = positions[p + 1];
      if (close - open > MAX_QUOTE_CHARS) {
        p -= 1; // resynchronise: treat this opener as spurious
        continue;
      }
      if (claim(open, close + 1)) push(open, close + 1, raw.slice(open + 1, close), 'double');
    }
  }

  found.sort((a, b) => a.start - b.start);
  found.forEach((q, i) => {
    q.id = i;
  });
  return found;
}
